import React, { useMemo, useState } from "react";
import { Box, Stack, Typography, Chip, Skeleton, Card, Button } from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import {
  useListMasteryApprovalsQuery,
  useApproveMasteryApprovalMutation,
  useRejectMasteryApprovalMutation,
} from "../../Api/attoDeskApi";
import { useNotifier } from "../../Core/Notifier";
import { Privileges } from "../../Core/Enum/enum";
import useUser from "../../Core/hook/useUser";
import useDebounce from "../../Components/SearchDebonce/useDebounce";
import { ApprovalRow, StatusFilter, PALETTE } from "./types";
import MasteryApprovalSummary from "./components/MasteryApprovalSummary";
import MasteryApprovalFilters from "./components/MasteryApprovalFilters";
import MasteryApprovalCard from "./components/MasteryApprovalCard";
import MasteryApprovalDetailsDrawer from "./components/MasteryApprovalDetailsDrawer";
import ApproveMasteryDialog from "./components/ApproveMasteryDialog";
import RejectMasteryDialog from "./components/RejectMasteryDialog";
import MasteryApprovalEmptyState from "./components/MasteryApprovalEmptyState";

/**
 * Supervisor mastery approvals workspace.
 *
 * All approve/reject actions go through the existing
 * MasteryApprovalController mutations. Row-lock + status re-check +
 * `pendingKey` UNIQUE index enforce idempotency server-side (double
 * approval is impossible even under concurrent clicks). Frontend
 * still disables the two decision buttons for the acting row while
 * the mutation is in flight so the operator can't spam clicks.
 */
const MasteryApprovals: React.FC = () => {
  const notifier = useNotifier();
  const { userHasRight } = useUser();
  const canApprove = userHasRight(Privileges.MasteryApprove);

  const [status, setStatus] = useState<StatusFilter>("pending");
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 300);

  const { data, isFetching, isLoading, isError, refetch } = useListMasteryApprovalsQuery({
    status,
    ...(search ? ({ search } as any) : {}),
  });
  const [approve, approveState] = useApproveMasteryApprovalMutation();
  const [reject, rejectState] = useRejectMasteryApprovalMutation();

  const rows: ApprovalRow[] = useMemo(() => ((data?.data as ApprovalRow[]) ?? []), [data]);
  const counts = (data as any)?.counts as { pending: number; approved: number; rejected: number; all: number } | undefined;

  const [drawerRow, setDrawerRow] = useState<ApprovalRow | null>(null);
  const [approveRow, setApproveRow] = useState<ApprovalRow | null>(null);
  const [rejectRow, setRejectRow] = useState<ApprovalRow | null>(null);
  const [busyRowId, setBusyRowId] = useState<number | null>(null);

  const openReview = (r: ApprovalRow) => setDrawerRow(r);
  const closeDrawer = () => setDrawerRow(null);
  const startApprove = (r: ApprovalRow) => { setDrawerRow(null); setApproveRow(r); };
  const startReject = (r: ApprovalRow) => { setDrawerRow(null); setRejectRow(r); };

  const runApprove = async (notes: string) => {
    if (!approveRow) return;
    setBusyRowId(approveRow.id);
    try {
      const res = await approve({ id: approveRow.id, notes }).unwrap();
      if ((res as any)?.status === false) {
        notifier.showMessage((res as any)?.message ?? "Approval failed.", "error");
      } else {
        notifier.showMessage("Mastery approval applied.", "success");
        setApproveRow(null);
      }
    } catch (e: any) {
      notifier.showMessage(e?.data?.message ?? "Approval failed.", "error");
    } finally {
      setBusyRowId(null);
    }
  };
  const runReject = async (notes: string) => {
    if (!rejectRow) return;
    setBusyRowId(rejectRow.id);
    try {
      const res = await reject({ id: rejectRow.id, notes }).unwrap();
      if ((res as any)?.status === false) {
        notifier.showMessage((res as any)?.message ?? "Rejection failed.", "error");
      } else {
        notifier.showMessage("Mastery progression rejected.", "success");
        setRejectRow(null);
      }
    } catch (e: any) {
      notifier.showMessage(e?.data?.message ?? "Rejection failed.", "error");
    } finally {
      setBusyRowId(null);
    }
  };

  return (
    <Box sx={{ bgcolor: PALETTE.pageBg, minHeight: "100vh", px: { xs: 2, md: 4 }, py: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1, color: PALETTE.slate, fontSize: 12 }}>
        <HomeOutlinedIcon sx={{ fontSize: 14 }} />
        <Typography sx={{ fontSize: 12 }}>ABA Therapy</Typography>
        <ChevronRightIcon sx={{ fontSize: 14 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: PALETTE.ink }}>Mastery Approvals</Typography>
      </Stack>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-end" }} justifyContent="space-between" sx={{ mb: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 42, height: 42, borderRadius: "12px", bgcolor: PALETTE.info.bg, color: PALETTE.navy, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 22 } }}>
            <GavelOutlinedIcon />
          </Box>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 800, color: PALETTE.ink, letterSpacing: "-0.01em" }}>Mastery Approvals</Typography>
              <Chip label="SUPERVISOR REVIEW" size="small" sx={{ bgcolor: PALETTE.info.bg, color: PALETTE.navy, fontWeight: 800, fontSize: 10, letterSpacing: "0.06em" }} />
            </Stack>
            <Typography sx={{ fontSize: 13, color: PALETTE.slate, mt: 0.25, maxWidth: 640 }}>
              Review targets that have met mastery criteria and approve or reject their next clinical progression.
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {/* Summary */}
      <Box sx={{ mb: 2 }}>
        <MasteryApprovalSummary counts={counts} loading={isLoading} />
      </Box>

      {/* Filter toolbar */}
      <Box sx={{ mb: 2 }}>
        <MasteryApprovalFilters
          status={status}
          onStatus={setStatus}
          search={searchInput}
          onSearch={setSearchInput}
          onRefresh={() => refetch()}
          isFetching={isFetching}
          counts={counts}
        />
      </Box>

      {/* Body */}
      {isLoading ? (
        <Stack spacing={2}>
          {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={220} />)}
        </Stack>
      ) : isError ? (
        <Card sx={{ p: 5, textAlign: "center", borderRadius: 2.5, border: `1px solid ${PALETTE.border}`, boxShadow: "none" }}>
          <Box sx={{ width: 44, height: 44, mx: "auto", borderRadius: "50%", bgcolor: PALETTE.rejected.bg, color: PALETTE.rejected.fg, display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5 }}>
            <ErrorOutlineOutlinedIcon />
          </Box>
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: PALETTE.ink, mb: 0.5 }}>Unable to load mastery approvals</Typography>
          <Typography sx={{ fontSize: 13, color: PALETTE.slate, mb: 2 }}>Please try again in a moment.</Typography>
          <Button variant="contained" onClick={() => refetch()} sx={{ textTransform: "none", fontWeight: 700, bgcolor: PALETTE.navy2, "&:hover": { bgcolor: PALETTE.navy } }}>Retry</Button>
        </Card>
      ) : rows.length === 0 ? (
        <MasteryApprovalEmptyState filter={status} />
      ) : (
        <Stack spacing={2}>
          {rows.map((row) => (
            <MasteryApprovalCard
              key={row.id}
              row={row}
              onReview={openReview}
              onApprove={canApprove && row.status === "pending" ? startApprove : undefined}
              onReject={canApprove && row.status === "pending" ? startReject : undefined}
              actionsBusy={busyRowId === row.id}
            />
          ))}
        </Stack>
      )}

      <MasteryApprovalDetailsDrawer
        row={drawerRow}
        open={!!drawerRow}
        onClose={closeDrawer}
        onApprove={canApprove ? startApprove : undefined}
        onReject={canApprove ? startReject : undefined}
        canApprove={canApprove}
      />
      <ApproveMasteryDialog
        row={approveRow}
        open={!!approveRow}
        onClose={() => setApproveRow(null)}
        onConfirm={runApprove}
        submitting={approveState.isLoading}
      />
      <RejectMasteryDialog
        row={rejectRow}
        open={!!rejectRow}
        onClose={() => setRejectRow(null)}
        onConfirm={runReject}
        submitting={rejectState.isLoading}
      />
    </Box>
  );
};

export default MasteryApprovals;
